import React, { useEffect } from 'react';
import EventReadMode from './EventReadMode';
import { EventBuilder } from '@src/emp-3.0/components/Metrics/components';
import { EEventType } from '@src/emp-3.0/types';
import { usePostMappedEvents, useUpdateMappedEvent } from '@api/emp/queryHooks';
import { message } from 'antd';
import { useEventListForCreateMetric } from '@src/emp-3.0/api/queryHooks';
import { isEqual } from 'lodash';
import { mappedEventDeployedStatus } from '@src/common/constant/enum';
import { buildEventByRawEvent } from 'emp-3.0/utils';
interface IProps {
  // event priority is higher than eventId
  eventId?: number | string; // string is for raw event
  event?: any; // MappedEventTypes; for eidting
  viewModeProps?: {
    onEdit?: (event: any) => void;
    onDelete?: (event: any) => void;
    onClone?: (event: any) => void;
    expanded?: boolean;
  };
  editModeProps?: {
    onSave?: (event: any) => void;
    onChange?: (event: any) => void;
    onCancel?: (event: any) => void;
  };
  type?: EEventType;
  style?: React.CSSProperties;
}

const EventItem: React.FC<IProps> = ({
  type,
  eventId,
  event,
  viewModeProps: { onEdit, onDelete, onClone } = {},
  editModeProps: { onSave, onCancel, onChange } = {},
  style,
}) => {
  const { data = [], isLoading } = useEventListForCreateMetric();
  const [expanded, setExpanded] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [eventValue, setEventValue] = React.useState<any>(null);
  const [eventJustSaved, setEventJustSaved] = React.useState<any>(null);

  useEffect(() => {
    if (event) {
      setEventValue(event);
      return;
    }
    if (!event && !eventId) {
      setEventValue(null);
      return;
    }
    const curEvent = Number(eventId)
      ? data.find((e) => e.id === eventId)
      : buildEventByRawEvent(eventId + '');
    if (!curEvent) {
      return;
    }
    const curEventValue = {
      event_name: curEvent.event_name,
      rules: curEvent.rules,
    };
    const newEventValue = {
      event_name: eventJustSaved?.event_name,
      rules: eventJustSaved?.rules,
    };
    // when save a event, will set event to null, but curEvent is the old one at this time
    // only for editing a existing event
    if (
      eventJustSaved &&
      eventId === eventJustSaved.event_id &&
      !isEqual(curEventValue, newEventValue)
    ) {
      return;
    }
    setEventValue(curEvent);
  }, [event, eventId, data, eventJustSaved]);
  useEffect(() => {
    if (eventValue?.event_id) {
      setEventJustSaved(null);
    }
  }, [eventValue]);

  useEffect(() => {
    if (event) {
      setExpanded(true);
      setIsEditing(true);
      return;
    }
    setIsEditing(false);
  }, [event, eventId]);

  const { mutate: create, isLoading: createMappedEventLoading } = usePostMappedEvents();
  const { mutate: update, isLoading: isUpdateMappedEventLoading } = useUpdateMappedEvent();

  const handleCancelClick = () => {
    onCancel?.(event);
  };

  const handleDelete = (event) => {
    onDelete?.(event);
  };

  const handleEdit = (event) => {
    onEdit?.(event);
  };

  const handleClone = (event) => {
    const newName = event.event_name + ' Copy';
    const newEvent: any = {
      ...event,
      event_name: newName,
      event_id: null,
      id: undefined,
      related_flows: [],
      related_metrics: [],
      status: mappedEventDeployedStatus.undeployed,
    };
    onClone?.(newEvent);
  };

  const handleEventChange = (event) => {
    // event type is MappedEventTypes or eventId
    // when event has duplicate rules with existing mapped events, will get a Action 'Use Existing Event', which will set event to eventId type
    onChange?.(event);
  };
  const handleSaveSuccess = (event) => {
    setEventJustSaved(event);
    onSave?.(event);
  };
  const handleSaveClick = (event) => {
    if (!event) return;
    const queryBody = {
      event_name: event.event_name,
      event_category: event.event_category,
      description: event.description,
      rules: event.rules,
    };
    if (event?.event_id) {
      update(
        { id: event?.event_id, mappedEvent: event },
        {
          onSuccess: ({ data }) => {
            message.success('Mapped Event Updated Successfully!').then();
            handleSaveSuccess({ ...event });
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg).then();
          },
        },
      );
    } else {
      create(
        { ...queryBody },
        {
          onSuccess: ({ data }) => {
            const newEvent = data?.[0];
            message.success('Mapped Event Created Successfully!').then();
            handleSaveSuccess({ ...newEvent, ...queryBody });
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg).then();
          },
        },
      );
    }
  };
  return eventValue ? (
    <div style={style}>
      {isEditing ? (
        <EventBuilder
          onSave={handleSaveClick}
          onCancel={handleCancelClick}
          onChange={handleEventChange}
          event={eventValue}
          isLoading={createMappedEventLoading || isUpdateMappedEventLoading || isLoading}
        />
      ) : (
        <EventReadMode
          event={eventValue}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClone={handleClone}
          expanded={expanded}
        />
      )}
    </div>
  ) : null;
};

export default EventItem;
